import type { MouseEvent } from 'react'
import { dispatchSubsDisclosure } from '@/msgdeskRuntime/subsDisclosure'
import { legacyContractDataAttrs, legacyHookAttrs, type LegacyContractName } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { RootSubsFeed } from '../subs/SubsFeed'
import { useSubsStore } from '@/state/MsgTreeProvider'
import {
  selectAccessGrant,
  selectAccessState,
  selectDeskIdentity,
  selectFolderSectionCount,
  selectPageSettingsState,
  selectSectionPaging,
} from '@/msgdeskRuntime/state'
import type { SubsStoreState } from '@/msgdeskRuntime/state'
import { usePanelsSurface } from './PanelsSurfaceContext'

// Subs toolbar (legacy e86-e90) and the two panels. AnswersPanel / MessagesPanel
// are kept as separate components per the agreed split; they share the surface
// control leaves (seam) rather than being merged.

function handleHideSubs(event: MouseEvent<HTMLElement>) {
  event.preventDefault()
  event.stopPropagation()
  dispatchSubsDisclosure({ action: 'toggle-all', target: event.currentTarget })
}

// CHECKING = POSSESSION: whoever possesses the desk (edit-unlocked; pinless
// desks are possessed by everyone, legacy exact) may check/move rows. The
// checked-info element must stay MOUNTED either way — the legacy checker
// writes counters into it through paging_els["checkeds_info"].
function selectCanCheckOrMove(state: SubsStoreState): boolean {
  return selectAccessGrant(state).capabilities.can_check_or_move
}

export function SubsToolbar() {
  const { skin } = usePanelsSurface()
  const { state } = useSubsStore()
  // §0.16: the toolbar language must not claim a Messages branch on an
  // answer desk (canonical fail-closed desk identity).
  const { canHaveSubmessages } = selectDeskIdentity(state)
  if (state.renderProjection.mode !== 'msg') return null
  // LOCKED-SCREEN: no subs chrome on a show/time-locked desk (the panels
  // already hide; the toolbar must too, so only the locked template shows).
  if (!selectAccessState(state).showUnlocked) return null

  const answersTotal = selectSectionPaging(state, false, 'msg').total
  const messagesTotal = canHaveSubmessages ? selectSectionPaging(state, true, 'msg').total : 0
  const hasAnswers = answersTotal > 0
  const hasMessages = messagesTotal > 0
  const label = hasAnswers && hasMessages
    ? 'Fold all (answers and msgs)'
    : hasMessages
      ? 'Fold all (msgs)'
      : 'Fold all (answers)'

  const toolbar = (
    // SKELETON: subs.toolbar is a legacy-selected toolbar target.
    // The md-* look can change, but the contract and event callbacks stay.
    <div {...legacyHookAttrs('subs.toolbar', `md-subs-toolbar ${skin.toolbar}`)}>
      <a
        {...legacyHookAttrs('subs.toolbar.hide', `md-subs-toolbar__hide ${skin.toolbarHide}`)}
        onClick={handleHideSubs}
      >
        {label}
      </a>
      <a {...legacyHookAttrs('subs.toolbar.message', `md-subs-toolbar__message ${skin.toolbarMessage}`)} />
    </div>
  )

  // Keep the contracted toolbar mounted so zero -> one mutations can reuse
  // the same legacy address; only its uncontracted wrapper is presentation.
  if (!hasAnswers && !hasMessages) {
    return <div aria-hidden="true" style={{ display: 'none' }}>{toolbar}</div>
  }
  return toolbar
}

export function AnswersPanel() {
  const { FoldSubsButton, PagingBar, RefreshSubs, CheckedsInfo, MoreLink, config, skin } = usePanelsSurface()
  const { state } = useSubsStore()
  const access = selectAccessState(state)
  const settings = selectPageSettingsState(state)
  const feedMode = state.renderProjection.mode === 'answers' ? 'answers' : 'msg'
  const canCheckOrMove = selectCanCheckOrMove(state)
  // PAGING VIEW-MODEL: one live selector; no local page math (doc 24 §9).
  const { total, currentPage, totalPages, moreCount } = selectSectionPaging(state, false)
  const isMainMode = state.renderProjection.mode === 'msg'
  const isFolded = isMainMode && currentPage === 1
    && (settings.foldAnswers || state.chromeByContract['subs.answers.rootList']?.visible === false)
  const isVisible = access.showUnlocked && state.renderProjection.mode !== 'submsges'

  const panel = (
    // SKELETON: answers panel/list contracts are legacy paging and mount targets.
    <div
      {...legacyHookAttrs('subs.answers.front', `comments md-subs-panel md-subs-panel--answers ${skin.panelAnswers}`)}
      style={{ display: isVisible ? 'block' : 'none' }}
    >
      {isMainMode
        ? <FoldSubsButton section={0} label="Answers" count={total || undefined} />
        : <SectionCountInfo label="Answers" count={total} />}
      <div {...legacyHookAttrs('subs.panelTools', `md-subs-panel__tools ${skin.panelTools}`)}>
        <RefreshSubs section={0} />
        {canCheckOrMove
          ? <CheckedsInfo section={0} visual="legacy" />
          : <span aria-hidden="true" style={{ display: 'none' }}><CheckedsInfo section={0} visual="legacy" /></span>}
      </div>
      <PagingBar section={0} currentPage={currentPage} totalPages={totalPages} />
      <ul {...legacyHookAttrs('subs.answers.rootList', `page1 md-subs-list md-subs-list--answers ${skin.rootList}`)} style={{ display: isFolded ? 'none' : undefined }}>
        <RootSubsFeed mode={feedMode} activeModes={['msg', 'subs', 'answers']} variant={config.mainTemplateVariant('answers')} />
      </ul>
      {isFolded ? null : <MoreLink section={0} moreCount={moreCount} />}
    </div>
  )

  // On the combined front page an empty section has no visible chrome. Keep
  // the contracted panel mounted so the first answer/quick reply can initialize
  // paging against the same node and React can reveal it from the live count.
  if (isMainMode && total < 1) {
    return <div aria-hidden="true" style={{ display: 'none' }}>{panel}</div>
  }
  return panel
}

export function MessagesPanel() {
  const { FoldSubsButton, PagingBar, RefreshSubs, CheckedsInfo, MoreLink, config, skin } = usePanelsSurface()
  const { state } = useSubsStore()
  const access = selectAccessState(state)
  const settings = selectPageSettingsState(state)
  const feedMode = state.renderProjection.mode === 'submsges' ? 'submsges' : 'msg'
  const canCheckOrMove = selectCanCheckOrMove(state)
  // PAGING VIEW-MODEL: one live selector; no local page math (doc 24 §9).
  const { total, currentPage, totalPages, moreCount } = selectSectionPaging(state, true)
  const isMainMode = state.renderProjection.mode === 'msg'
  const isFolded = isMainMode && currentPage === 1
    && (settings.foldSubmsges || state.chromeByContract['subs.submsges.rootList']?.visible === false)
  // §0.16: an answer desk has no Messages branch — the panel stays MOUNTED
  // (legacy paging/mount target contract) but never becomes visible there.
  // The identity gate must live on an UNCONTRACTED wrapper below: legacy's
  // section show (jQuery-style .show() through the bridge) writes an inline
  // display value straight onto the front element after boot, and React does
  // not re-write a style it believes unchanged — so gating only the front
  // element's own style loses to the imperative legacy write.
  const { canHaveSubmessages } = selectDeskIdentity(state)
  const isVisible = canHaveSubmessages && access.showUnlocked && state.renderProjection.mode !== 'answers'

  const panel = (
    // SKELETON: messages panel/list contracts are legacy paging and mount targets.
    <div
      {...legacyHookAttrs('subs.submsges.front', `compact md-subs-panel md-subs-panel--submsges ${skin.panelSubmsges}`)}
      style={{ display: isVisible ? 'block' : 'none' }}
    >
      {isMainMode
        ? <FoldSubsButton section={1} label="Messages" count={total || undefined} />
        : <SectionCountInfo label="Messages" count={total} />}
      <div {...legacyHookAttrs('subs.panelTools', `md-subs-panel__tools ${skin.panelTools}`)}>
        <RefreshSubs section={1} />
        {canCheckOrMove
          ? <CheckedsInfo section={1} visual="legacy" />
          : <span aria-hidden="true" style={{ display: 'none' }}><CheckedsInfo section={1} visual="legacy" /></span>}
      </div>
      <PagingBar section={1} currentPage={currentPage} totalPages={totalPages} />
      <ul {...legacyHookAttrs('subs.submsges.rootList', `page1 md-subs-list md-subs-list--submsges ${skin.rootList}`)} style={{ display: isFolded ? 'none' : undefined }}>
        <RootSubsFeed mode={feedMode} activeModes={['msg', 'subs', 'submsges']} isSubmsges variant={config.mainTemplateVariant('submsges')} />
      </ul>
      {isFolded ? null : <MoreLink section={1} moreCount={moreCount} />}
    </div>
  )

  if (!canHaveSubmessages || (isMainMode && total < 1)) {
    return <div aria-hidden="true" style={{ display: 'none' }}>{panel}</div>
  }
  return panel
}

function SectionCountInfo({ label, count }: { label: string; count: number }) {
  const { skin } = usePanelsSurface()
  return (
    <span className={`md-menu-subs-info ${skin.sectionInfo}`}>
      {label}
      <span {...legacyContractDataAttrs('subs.menuToggle.count')} className={`md-menu-subs-info__count ${skin.sectionInfoCount}`}>({count})</span>
    </span>
  )
}

function MenuModePanel({
  mode,
  isSubmsges = false,
  isSelected,
}: {
  mode: 'drafts' | 'trash' | 'favs'
  isSubmsges?: boolean
  isSelected: boolean
}) {
  const { PagingBar, RefreshSubs, CheckedsInfo, MoreLink, config, skin } = usePanelsSurface()
  const { state } = useSubsStore()
  const section = isSubmsges ? 1 : 0
  const isActiveMode = state.renderProjection.mode === mode
  const canCheckOrMove = selectCanCheckOrMove(state)
  // PAGING VIEW-MODEL: one live selector; no local page math (doc 24 §9).
  const { total, currentPage, totalPages, moreCount } = selectSectionPaging(state, isSubmsges, mode)
  const panelContract: LegacyContractName = mode === 'favs'
    ? (isSubmsges ? 'subs.favs.submsgesFront' : 'subs.favs.answersFront')
    : mode === 'trash'
      ? (isSubmsges ? 'subs.trash.submsgesFront' : 'subs.trash.answersFront')
      : (isSubmsges ? 'subs.drafts.submsgesFront' : 'subs.drafts.answersFront')
  const modeClass = isSubmsges ? 'compact' : 'comments'
  const subsSection = isSubmsges ? 'submsges' : 'answers'
  const listContract: LegacyContractName = isSubmsges ? 'subs.menuMode.submsgesList' : 'subs.menuMode.answersList'
  const visible = isActiveMode && isSelected

  return (
    // SKELETON: menu-mode panel/list contracts are legacy mount targets.
    <div
      {...legacyHookAttrs(panelContract, `${modeClass} md-subs-panel md-subs-panel--${mode} ${skin.menuPanel} ${visible ? skin.menuPanelShown : skin.menuPanelHidden}`)}
      style={{ display: visible ? 'flex' : 'none' }}
    >
      {/* Folder content is stripped to paging + subs + the selected-items bar.
          The count badge, photo toggle, and refresh stay in the DOM (legacy
          count/photo/refresh addressing) but are hidden — see runtime
          contracts §10: these mcss-* names are addressed, not decorative. */}
      <div className="md-subs-panel__legacy-hidden" aria-hidden="true" style={{ display: 'none' }}>
        <SectionCountInfo label={isSubmsges ? 'Messages' : 'Answers'} count={total} />
        {!isSubmsges && <a href="#" {...legacyHookAttrs('subs.togglePhoto', `md-subs-toggle-photo ${skin.togglePhoto}`)} />}
      </div>
      <div {...legacyHookAttrs('subs.panelTools', `md-subs-panel__tools ${skin.menuPanelTools}`)}>
        <span aria-hidden="true" style={{ display: 'none' }}><RefreshSubs section={section} /></span>
        {canCheckOrMove
          ? <CheckedsInfo section={section} mode={mode} visual="legacy" />
          : <span aria-hidden="true" style={{ display: 'none' }}><CheckedsInfo section={section} mode={mode} visual="legacy" /></span>}
      </div>
      <PagingBar section={section} currentPage={currentPage} totalPages={totalPages} registerContract={false} />
      <ul
        {...legacyContractDataAttrs(listContract)}
        className={`page1 md-subs-list ${skin.menuList} ${isSubmsges ? 'md-subs-list--submsges' : 'md-subs-list--answers'}`}
      >
        {/* Only the visible section renders rows. The other section list
            stays mounted (legacy contract) but empty, so it can never bleed
            into the shown section even if legacy imperatively toggles the
            panel div. */}
        {visible ? <RootSubsFeed mode={mode} activeModes={[mode]} isSubmsges={isSubmsges} variant={config.menuTemplateVariant(mode, subsSection)} /> : null}
      </ul>
      <MoreLink section={section} moreCount={moreCount} />
    </div>
  )
}

export function MenuSubsPanels({ mode }: { mode: 'drafts' | 'trash' | 'favs' }) {
  const { menuEmptyLabel, skin } = usePanelsSurface()
  const { state } = useSubsStore()
  const answersCount = selectFolderSectionCount(state, mode, false)
  const msgesCount = selectFolderSectionCount(state, mode, true)

  // ANSWER-DESK RULE (§0.16, canonical selectDeskIdentity): ALL of an
  // answer's folders — bookmarks included — can only hold answer-type subs
  // (only messages can log in and star, so even the favs bucket never
  // receives a message row). They present a single (answers) list with no
  // section choice. Both MenuModePanels stay mounted regardless (they are
  // legacy paging/mount targets) — only visibility is gated.
  const { mainIsMsg } = selectDeskIdentity(state)
  const withSections = mainIsMsg

  // EMPTY: a folder with no subs in either section shows ONLY the notice
  // (the panels remain mounted for legacy addressing, but hidden).
  const isEmpty = answersCount <= 0 && msgesCount <= 0

  // The section is chosen ENTIRELY from the menu flyout sub-buttons (Sidebar
  // FolderNavItem) — there are no in-content section tabs. Content is strictly
  // one section: only the active section's panel renders visible; the other
  // stays mounted-but-hidden for legacy paging/mount addressing.
  const activeSection = withSections ? state.renderProjection.activeSection : 'answers'
  const answersSelected = !isEmpty && activeSection === 'answers'
  const submsgesSelected = !isEmpty && withSections && activeSection === 'submsges'

  return (
    <>
      {isEmpty ? <div className={`md-menu-empty ${skin.menuEmpty ?? ''}`}>{menuEmptyLabel}</div> : null}
      <MenuModePanel mode={mode} isSelected={answersSelected} />
      <MenuModePanel mode={mode} isSubmsges isSelected={submsgesSelected} />
    </>
  )
}
